import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import { motion } from 'framer-motion';
import type { Perk } from '../../types/eventBookingCategoryTypes';

interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
}

interface MobileBookingCategoryHeroProps {
  eventBookingCategory: {
    id: string;
    booking_category: {
      name: string;
      description: string;
      base_image_id: string | null;
      base_video_id?: string | null;
      perks?: Perk[];
      media?: { media_id: string; media_type: string; rank: number }[];
      business?: {
        handle: string;
      } | null;
    };
    price: number;
    currency?: string;
  };
  /** Whether this is a table booking to determine "per seat" vs "per ticket" */
  isTableBooking?: boolean;
}

const IMAGE_DURATION_MS = 3000;

export const MobileBookingCategoryHero: React.FC<MobileBookingCategoryHeroProps> = ({
  eventBookingCategory,
  isTableBooking = false
}) => {
  const { booking_category, price, currency } = eventBookingCategory;
  const { name, description, base_image_id, base_video_id, media } = booking_category;

  // Format price
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  // Compile media items
  const mediaItems = useMemo(() => {
    const items: MediaItem[] = [];

    // Base media first (prioritize video over image if both exist as "base", though usually it's one or the other)
    if (base_video_id && base_video_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: base_video_id, url: getSignedMediaUrl(base_video_id, 'video').url, type: 'video' });
    } else if (base_image_id && base_image_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: base_image_id, url: getSignedMediaUrl(base_image_id, 'image').url, type: 'image' });
    }

    // Then other media, sorted by rank
    if (media && media.length > 0) {
      const sortedMedia = [...media].sort((a, b) => a.rank - b.rank);
      sortedMedia.forEach(m => {
        // Skip if it's already the base media
        if (m.media_id === base_image_id || m.media_id === base_video_id) return;
        
        const type = (m.media_type || '').toLowerCase().startsWith('video') ? 'video' : 'image';
        items.push({ id: m.media_id, url: getSignedMediaUrl(m.media_id, type).url, type });
      });
    }

    // Fallback if absolutely nothing is found
    if (items.length === 0) {
      items.push({ id: 'fallback', url: '', type: 'image' });
    }

    return items;
  }, [base_image_id, base_video_id, media]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<number>(0);

  const activeItem = mediaItems[currentIndex];
  const isVideo = activeItem.type === 'video';

  // Handle advancing to next slide
  const advanceToNext = () => {
    setCurrentIndex(prev => (prev + 1) % mediaItems.length);
    setProgress(0);
  };

  const skipToPrevious = () => {
    setCurrentIndex(prev => (prev - 1 + mediaItems.length) % mediaItems.length);
    setProgress(0);
  };

  // Image auto-advance and progress animation
  useEffect(() => {
    if (isVideo) return; // Videos handle their own progress via onTimeUpdate
    if (activeItem.id === 'fallback') return;

    let start: number | null = null;
    
    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const newProgress = Math.min((elapsed / IMAGE_DURATION_MS) * 100, 100);
      
      setProgress(newProgress);

      if (elapsed >= IMAGE_DURATION_MS) {
        advanceToNext();
      } else {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    // Need to reset progress to 0 before starting new animation
    setProgress(0);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [currentIndex, isVideo, activeItem.id, mediaItems.length]); // Dependencies include currentIndex to restart animation

  // Reset progress when index changes
  useEffect(() => {
    if (videoRef.current && isVideo) {
      setProgress(0);
      videoRef.current.currentTime = 0;
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Video auto-play failed", error);
          // Auto-advance if video fails to play to prevent getting stuck
          advanceToNext();
        });
      }
    }
  }, [currentIndex, isVideo]);

  // Video progress handling
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const duration = videoRef.current.duration || 1; // avoid NaN
      setProgress((current / duration) * 100);
    }
  };

  return (
    <div className="w-full relative">
      <div 
        className="relative w-full aspect-[4/3] bg-[#050505] overflow-hidden shrink-0"
      >
        
        {/* Background Media */}
        <div className="absolute inset-0">
          {activeItem.id === 'fallback' ? (
            <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' }} />
          ) : isVideo ? (
            <video
              ref={videoRef}
              src={activeItem.url}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
              loop={false}
              onTimeUpdate={handleTimeUpdate}
              onEnded={advanceToNext}
              onError={(e) => {
                console.error("Video element error", e);
                advanceToNext();
              }}
            />
          ) : (
            <div 
              className="w-full h-full transition-opacity duration-300"
              style={{
                backgroundImage: `url(${activeItem.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          )}
        </div>

        {/* Gradient overlays to blur into the #050505 background at top and bottom */}
        {/* Top fade (heavy for text legibility) */}
        <div className="absolute inset-x-0 top-0 h-[60%] bg-gradient-to-b from-[#050505]/95 via-[#050505]/70 to-transparent pointer-events-none z-0" />
        
        {/* Bottom fade (for pagination dots legibility) */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none z-0" />

        {/* Tap areas for navigation */}
        {mediaItems.length > 1 && (
          <div className="absolute inset-0 z-10 flex">
            <div className="w-1/2 h-full" onClick={skipToPrevious} />
            <div className="w-1/2 h-full" onClick={advanceToNext} />
          </div>
        )}

        {/* Content Container */}
        <div className="absolute z-20 inset-0 p-5 flex flex-col justify-between pointer-events-none">
          
          {/* TOP ROW: Name & Price */}
          <div className="flex justify-between items-start gap-4">
            {/* Left: Component Info */}
            <div className="flex-1 flex flex-col">
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white leading-tight drop-shadow-md pb-1">
                {name}
              </h3>
              <p className="text-sm sm:text-base font-medium text-gray-300 drop-shadow-sm line-clamp-2 leading-snug">
                {description}
              </p>
            </div>

            {/* Right: Price */}
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-2xl sm:text-3xl font-black text-white drop-shadow-md">
                {formatPrice(price || 0)}
              </span>
              <span className="text-[11px] font-bold tracking-wide uppercase text-gray-300 drop-shadow-sm mt-0.5">
                {isTableBooking ? 'per seat' : 'per ticket'}
              </span>
            </div>
          </div>

          {/* Progress Indicators (Pill shape for active, circle for inactive) */}
          {mediaItems.length > 1 && (
            <div className="flex justify-center gap-1.5 w-full mt-2 items-center">
              {mediaItems.map((_, idx) => {
                const isActive = idx === currentIndex;
                const isPast = idx < currentIndex;
                return (
                  <motion.div
                    key={idx}
                    className="h-2 rounded-full overflow-hidden bg-white/30 backdrop-blur-sm"
                    initial={false}
                    animate={{
                      width: isActive ? 24 : 8,
                      opacity: isActive ? 1 : isPast ? 0.7 : 0.4
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {isActive && (
                      <motion.div
                        className="h-full bg-white"
                        style={{ width: `${progress}%` }}
                        transition={{ duration: 0.1, ease: "linear" }}
                      />
                    )}
                    {isPast && (
                      <div className="w-full h-full bg-white" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
